import { parseJsonRecovering } from '../../shared/utils/jsonRecovery';

interface FormAnswers {
  TemplateFormId?: number;
  /** The answers as JSON text, as the ticket stores them and the dialog sends them. */
  CardAnswerJson?: string;
}

/** An answer left empty - the form's own file box comes back as "" where the stored answers have no key at all. */
const isEmpty = (value: unknown) => value === '' || value == null;

/** Array.sort's own order (UTF-16 code units). */
const byCodeUnit = (a: string, b: string): number => {
  if (a < b) return -1;
  return a > b ? 1 : 0;
};

/**
 * The same JSON whatever the order of its keys, without its empty answers: the stored answers and the ones about to be
 * sent are written separately.
 */
const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical);
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  return Object.keys(record).sort(byCodeUnit)
    .filter(key => !isEmpty(record[key]))
    .reduce((sorted, key) => ({ ...sorted, [key]: canonical(record[key]) }), {});
};

const read = (json: string | undefined): unknown => {
  try {
    return canonical(parseJsonRecovering(json ?? '{}'));
  } catch {
    return undefined;
  }
};

interface SavingAnswers extends FormAnswers {
  /**
   * The same answers before formatInputRawData, which rewrites a date ("2024-07-30") as a time with the agent's offset.
   * A form the ticket last saved elsewhere holds the first, one last saved through the dialog the second - and one
   * ticket can hold both, form by form - so each form may match either.
   */
  CardAnswerJsonBeforeFormat?: string;
}

const same = (json: string | undefined, stored: unknown) => {
  const now = read(json);
  return now !== undefined && JSON.stringify(now) === JSON.stringify(stored);
};

/**
 * Whether the answers about to be saved are the ones the ticket already holds - the same forms, however many, each
 * with the same answers. An answer that cannot be read, or a form only one side has, counts as a change, so the save
 * goes ahead.
 */
export const answersUnchanged = (saving: SavingAnswers[], stored: FormAnswers[]): boolean => {
  if (saving.length !== stored.length) return false;
  return saving.every(form => {
    const before = stored.find(other => other.TemplateFormId === form.TemplateFormId);
    if (!before) return false;
    const held = read(before.CardAnswerJson);
    return same(form.CardAnswerJson, held) || (form.CardAnswerJsonBeforeFormat !== undefined && same(form.CardAnswerJsonBeforeFormat, held));
  });
};
